import React from 'react';
import {
  DataGrid,
  Icon,
  IconSize,
  IconType,
  NavigationItem,
  SmallBodyText,
  SpaceWrapper,
  TinyBodyText,
  Tooltip,
} from 'ui-toolkit';
import styled from 'styled-components';
import { ClientUrlFactory } from '../../ui-toolkit/rest/ClientUrlFactory';
import { DisplayTransactionCategory, eTransactionCategory } from '../../ui-toolkit/enums/eTransactionType';
import { eExternalOrderStatus } from '../../ui-toolkit/enums/eExternalOrderStatus';
import { prettyInternalOrderStatus } from '../../ui-toolkit/enums/eInternalOrderStatus';
import { addressDataToString } from '../../ui-toolkit/orders/interfaces/IAddressResponse';
import {
  eSearchOrdersOrderBy,
  getOrderByFromSearchOrders,
  getSearchOrdersOrderBy,
} from '../../ui-toolkit/orders/searchOrder/types/eSearchOrdersOrderBy';
import { getBorrowerStringFromParties, ISearchOrderData } from '../../ui-toolkit/orders/searchOrder/types/ISearchOrder';
import { DateFormat, getDateString } from '../../ui-toolkit/utilities/getDateString';

interface IOrdersTableProps {
  orders: ISearchOrderData[];
  sorting: eSearchOrdersOrderBy;
  loading: boolean;
  onSortChange: (order: eSearchOrdersOrderBy) => void;
}

export const OrdersTable: React.FC<IOrdersTableProps> = ({ orders, onSortChange, loading, sorting }) => {
  return (
    <DataGrid
      columns={[
        {
          dataField: 'fileNumber',
          text: 'Order Id',
          classes: (cell, row) => `fileNumber-column-${row.id}`,
          formatter: (cell, data?: ISearchOrderData) =>
            cell &&
            data && (
              <div>
                <SmallNavigationItem
                  text={cell as string}
                  to={ClientUrlFactory().urlFor.orderStatus(data.id)}
                  isInline
                  showUnderline
                />
                {data.clientCompany.sprucePowered ? (
                  <SpaceWrapperVertMiddle leftSpace={'none'} rightSpace={'none'} topSpace={'none'} bottomSpace={'none'}>
                    <Tooltip
                      tooltipContent={<TinyBodyText>SprucePowered Order</TinyBodyText>}
                      placement={'right'}
                      trigger={'hover'}
                      offset={'.3rem'}
                    >
                      <SpaceWrapper leftSpace={'default'} rightSpace={'none'} topSpace={'none'} bottomSpace={'none'}>
                        <Icon type={IconType.SprucePowered} size={IconSize.Small} />
                      </SpaceWrapper>
                    </Tooltip>
                  </SpaceWrapperVertMiddle>
                ) : null}
              </div>
            ),
          sort: false,
        },
        {
          dataField: 'createdAt',
          text: 'Create At',
          sort: true,
          formatter: (cell) => <SmallBodyText>{getDateString(DateFormat.short, cell as string, true)}</SmallBodyText>,
          classes: (cell, row) => `createdAt-column-${row.id}`,
        },
        {
          dataField: 'parties',
          text: 'Name(s)',
          classes: (cell, row) => `parties-column-${row.id}`,
          formatter: (cell, data) => (
            <SmallBodyText>{data && data.parties && getBorrowerStringFromParties(data.parties)}</SmallBodyText>
          ),
          sort: false,
        },
        {
          dataField: 'propertyAddress.streetAddress',
          text: 'Address',
          classes: (cell, row) => `propertyAddress-column-${row.id}`,
          formatter: (_, data) =>
            data && data.propertyAddress && <SmallBodyText>{addressDataToString(data.propertyAddress)}</SmallBodyText>,
          sort: false,
        },
        {
          dataField: 'loanNumber',
          text: 'Loan Number',
          classes: (cell, row) => `loanNumber-column-${row.id}`,
          formatter: (cell, row) => <SmallBodyText>{row.loanNumber}</SmallBodyText>,
          sort: false,
        },
        {
          dataField: 'orderGroupId',
          text: 'Group ID',
          sort: false,
          classes: (cell, row) => `orderGroupId-column-${row.id}`,
          formatter: (cell, data) => (
            <StyledSmallBodyText>{data?.mobilityGroup?.externalId || data?.orderGroupId}</StyledSmallBodyText>
          ),
        },
        {
          dataField: 'transactionCategory',
          text: 'Transaction Category',
          sort: true,
          classes: (cell, row) => `transactionCategory-column-${row.id}`,
          formatter: (cell) =>
            cell && <SmallBodyText>{DisplayTransactionCategory[cell as eTransactionCategory]}</SmallBodyText>,
        },
        {
          dataField: 'lenderCompany.name',
          text: 'Lender',
          sort: true,
          classes: (cell, row) => `lenderCompany-column-${row.id}`,
          formatter: (cell, data) =>
            cell &&
            data && (
              <SmallNavigationItem
                text={data.lenderCompany.name || ''}
                href={ClientUrlFactory().urlFor.lenderCompany((data.lenderCompany && data.lenderCompany.id) || '')}
                isInline
                showUnderline
              />
            ),
        },
        {
          dataField: 'clientCompany.name',
          text: 'Client',
          sort: true,
          classes: (cell, row) => `clientCompany-column-${row.id}`,
          formatter: (cell, data) =>
            cell &&
            data && (
              <SmallNavigationItem
                text={data.clientCompany.name || ''}
                href={ClientUrlFactory().urlFor.clientCompany(data.clientCompany.id || '')}
                isInline
                showUnderline
              />
            ),
        },
        {
          dataField: 'orderPhase',
          text: 'Status',
          sort: true,
          classes: (cell, row) => `orderPhase-column-${row.id}`,
          formatter: (cell) =>
            cell && <SmallBodyText>{prettyInternalOrderStatus[cell as eExternalOrderStatus]}</SmallBodyText>,
        },
        {
          dataField: 'closesOn',
          text: 'Closing Date',
          sort: true,
          classes: (cell, row) => `closesOn-column-${row.id}`,
          formatter: (cell) => cell && <SmallBodyText>{getDateString(DateFormat.short, cell as string)}</SmallBodyText>,
        },
      ]}
      data={orders}
      keyField="id"
      loading={loading}
      remote={true}
      onTableChange={(type, tableState) => {
        if (type === 'sort') {
          const orderBy = getSearchOrdersOrderBy(tableState.sortField, tableState.sortOrder as 'asc' | 'desc');
          if (orderBy) {
            onSortChange(orderBy);
          }
        }
      }}
      sort={getOrderByFromSearchOrders(sorting)}
    />
  );
};

const SpaceWrapperVertMiddle = styled(SpaceWrapper)`
  vertical-align: middle;
  display: inline-flex;
`;

const SmallNavigationItem = styled(NavigationItem)`
  ${(props) => props.theme.fonts.smallSize};
`;

const StyledSmallBodyText = styled(SmallBodyText)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 140px;
`;
